import { documentsApiRef } from '@agilelab/plugin-wb-builder-catalog';
import {
  Acceptance_type,
  WitboostDocument,
} from '@agilelab/plugin-wb-documents-common';
import {
  customAlertApiRef,
  WbButton,
  WbNoData,
} from '@agilelab/plugin-wb-platform';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@mainstage/core-plugin-api';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme,
} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import GetAppIcon from '@material-ui/icons/GetApp';
import { CookieAuthRefreshProvider } from '@mainstage/plugin-auth-react';
import { useEffect, useState } from 'react';
import { DocumentsAcceptanceActions } from './AcceptanceActions';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialog: {
      '& .MuiDialog-paper': {
        height: 'calc(100vh - 64px)',
        maxHeight: 'calc(100vh - 64px)',
        margin: '32px auto',
      },
    },
    dialogContent: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      padding: '16px',
    },
    container: {
      background: theme.palette.background.paper,
      padding: '0px',
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      alignSelf: 'stretch',
    },
    pdfContainer: {
      position: 'relative',
      width: '100%',
      flexGrow: 1,
      minHeight: '500px',
    },
    actionBox: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingLeft: theme.spacing(2),
    },
    page: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
  }),
);

export function DocumentsPopup(_props: {}) {
  const documentsApi = useApi(documentsApiRef);
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(customAlertApiRef);
  const configApi = useApi(configApiRef);
  const theme = useTheme();
  const classes = useStyles();

  const [showDocumentsPopup, setShowDocumentsPopup] = useState(false);
  const [documents, setDocuments] = useState<WitboostDocument[]>([]);
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [isPdfLoading, setPdfLoading] = useState<boolean>(true);
  const [documentUrl, setDocumentUrl] = useState<string | undefined>();
  const [objectRef, setObjectRef] = useState<HTMLObjectElement | null>(null);

  const currentDocument = documents[currentDocumentIndex];

  useEffect(() => {
    if (!currentDocument?.id) {
      setDocumentUrl(undefined);
      return undefined;
    }
    let cancelled = false;
    documentsApi.getDocumentUrl(currentDocument.id).then(url => {
      if (!cancelled) {
        setDocumentUrl(url);
      }
      setPdfLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [documentsApi, currentDocument]);

  // `error` isn't a recognized JSX prop for <object>, so both events are attached natively here.
  useEffect(() => {
    if (!objectRef) {
      return undefined;
    }
    const handleLoad = () => setPdfLoading(false);
    const handleError = () => setPdfLoading(false);

    objectRef.addEventListener('load', handleLoad);
    objectRef.addEventListener('error', handleError);
    return () => {
      objectRef.removeEventListener('load', handleLoad);
      objectRef.removeEventListener('error', handleError);
    };
  }, [objectRef]);

  useEffect(() => {
    setPdfLoading(true);
  }, [currentDocumentIndex]);

  const handleAcceptClick = async (): Promise<void> => {
    if (currentDocument) {
      try {
        setLoading(true);
        await documentsApi.acceptDocumentFromUser(currentDocument.id!);

        if (currentDocumentIndex === documents.length - 1) {
          setShowDocumentsPopup(false);
          return;
        }
        setCurrentDocumentIndex(prevIndex => prevIndex + 1);
      } catch (error) {
        alertApi.post({
          message: `Error accepting document ${currentDocument.name}. Contact the platform team for more info.`,
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleReject = () => {
    identityApi.signOut();
  };

  const handleDownloadClick = async (): Promise<void> => {
    if (!currentDocument?.id) {
      return;
    }
    try {
      const link = document.createElement('a');
      link.href = await documentsApi.getDocumentUrl(currentDocument.id);
      link.download = `${currentDocument.name}.pdf`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
    } catch (error) {
      alertApi.post({
        message: `Error downloading document ${currentDocument.name}. Contact the platform team for more info.`,
        severity: 'error',
      });
    }
  };

  useEffect(() => {
    const documentsEnabled: boolean =
      configApi.getOptionalBoolean(
        'mesh.documents.requireDocumentAcceptance',
      ) ?? false;
    try {
      if (!documentsEnabled) {
        setDocuments([]);
        setShowDocumentsPopup(false);
        return;
      }
      const fetchData = async () => {
        const fetchedDocuments = await documentsApi.retrieveDocuments({
          not_accepted_only: true,
          enabled_only: true,
          acceptance_types: [
            Acceptance_type.MANDATORY,
            Acceptance_type.ACKNOWLEDGMENT,
          ],
        });

        setDocuments(fetchedDocuments);
        if (fetchedDocuments.length > 0) {
          setShowDocumentsPopup(true);
        }
      };

      fetchData();
    } catch (error) {
      alertApi.post({
        error: error,
        severity: 'error',
      });
      setShowDocumentsPopup(false);
    }
  }, [identityApi, documentsApi, alertApi, configApi]);

  if (!showDocumentsPopup) {
    return null;
  }

  return (
    <Dialog
      open={showDocumentsPopup}
      className={classes.dialog}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle color="primary">{currentDocument?.name}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <DialogContentText id="alert-dialog-description">
          Read and express your acceptance of the following document.
        </DialogContentText>
        {currentDocument && (
          <Box className={classes.container}>
            <CookieAuthRefreshProvider pluginId="documents">
              <div className={classes.pdfContainer}>
                {isPdfLoading && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    <CircularProgress />
                  </div>
                )}

                <object
                  ref={setObjectRef}
                  data={documentUrl}
                  type="application/pdf"
                  aria-label={`${currentDocument.name} PDF preview`}
                  style={{
                    border: 'none',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    maxWidth: '100%',
                  }}
                  onLoad={() => setPdfLoading(false)}
                >
                  <WbNoData
                    fillContainer
                    showIcon={false}
                    header="Preview not available in this browser"
                    text={
                      <Typography>
                        Your browser doesn't support inline PDF viewing.
                        Download the document to review it before accepting.
                      </Typography>
                    }
                    primaryAction={
                      <WbButton
                        variant="contained"
                        color="primary"
                        icon={<GetAppIcon />}
                        onClick={handleDownloadClick}
                        label="Download PDF"
                      />
                    }
                  />
                </object>
              </div>
            </CookieAuthRefreshProvider>
          </Box>
        )}
      </DialogContent>
      <Box className={classes.actionBox}>
        <Typography>
          Document {currentDocumentIndex + 1} of {documents.length}
        </Typography>
        <Box padding={theme.spacing(0.1)} style={{ textAlign: 'center' }}>
          {currentDocument?.name}
        </Box>
        <DialogActions>
          <DocumentsAcceptanceActions
            type={currentDocument?.acceptance_type}
            onAcceptAction={handleAcceptClick}
            onDeclineAction={handleReject}
            loading={loading}
          />
        </DialogActions>
      </Box>
    </Dialog>
  );
}
